import { DataTable } from '@/components/data-table'
import { useCampaignClient } from '@/lib/api/ts-rest-client'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui'
import {
  Eraser,
  PaperPlaneTilt,
  PencilSimple,
  Plus,
  Spinner,
  Trash,
} from '@phosphor-icons/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

// Infer EndUser type from the contract
type EndUser = {
  id: string
  campaignId?: string | null
  email?: string | null
  firstName?: string | null
  middle?: string | null
  lastName?: string | null
  company?: string | null
  address?: any
  birthday?: string | null
  campaignIncentiveId?: string | null
  questionnaireAnswers?: any
  referralOf?: string | null
  happinessAnswers?: any
  phoneNumber?: string | null
  sku?: string | null
  digitalCode?: string | null
  postnlCode?: string | null
  referenceCode?: number | null
  uniqueCode?: string | null
  isTest?: boolean | null
}

export default function EndUsersTest({ isTestDisabled }: { isTestDisabled: boolean }) {
  const { id: campaignId } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const campaignClient = useCampaignClient()
  const [isAddingUser, setIsAddingUser] = useState(false)
  const [isEditingUser, setIsEditingUser] = useState(false)
  const [formData, setFormData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    sku: '',
  })

  // Query to fetch end users
  const {
    data: endUsersResponse,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['endUsers', campaignId],
    queryFn: async () => {
      if (!campaignId) return null
      return await campaignClient.endUsers.query({
        params: { campaignId },
      })
    },
    enabled: !!campaignId,
  })

  // Query to fetch campaign data for SKUs
  const { data: campaignResponse } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      if (!campaignId) return null
      return await campaignClient.getById.query({
        params: { id: campaignId },
      })
    },
    enabled: !!campaignId,
  })

  const campaign =
    campaignResponse?.status === 200 ? campaignResponse.body : null

  // Get available SKUs from campaign incentives
  const availableSKUs =
    campaign?.campaignIncentives
      ?.filter(
        (incentive: any) =>
          incentive?.incentiveItem?.skuCode &&
          !incentive?.referral &&
          incentive?.isVisible,
      )
      .map((incentive: any) => ({
        skuCode: incentive.incentiveItem.skuCode as string,
        name: (incentive.incentiveItem.name ||
          incentive.incentiveItem.skuCode) as string,
      })) || []

  // Mutation to add a single end user
  const addSingleEndUserMutation = useMutation({
    mutationFn: async (endUser: {
      campaignId: string
      firstName: string
      lastName: string
      email?: string | null
      sku?: string | null
    }) => {
      return await campaignClient.addSingleEndUser.mutate({
        params: { campaignId: endUser.campaignId },
        body: endUser,
      })
    },
    onSuccess: () => {
      resetForm()
      setIsAddingUser(false)
      refetch()
    },
  })

  // Mutation to update a test user
  const updateTestEndUserMutation = useMutation({
    mutationFn: async (endUser: {
      id: string
      firstName: string
      lastName: string
      email?: string | null
      sku?: string | null
    }) => {
      return await campaignClient.updateTestEndUser.mutate({
        params: { id: endUser.id },
        body: endUser,
      })
    },
    onSuccess: () => {
      resetForm()
      setIsEditingUser(false)
      refetch()
    },
  })

  // Mutation to delete a test user
  const deleteTestEndUserMutation = useMutation({
    mutationFn: async (endUserId: string) => {
      return await campaignClient.deleteTestEndUser.mutate({
        params: { id: endUserId },
      })
    },
    onSuccess: () => {
      refetch()
    },
  })

  // Mutation to clear a test user's answers
  const clearTestEndUserAnswersMutation = useMutation({
    mutationFn: async (endUserId: string) => {
      return await campaignClient.clearTestEndUserAnswers.mutate({
        params: { id: endUserId },
        body: {},
      })
    },
    onSuccess: () => {
      refetch()
    },
  })

  // Mutation to send a test email
  const sendTestEmailMutation = useMutation({
    mutationFn: async (endUserId: string) => {
      return await campaignClient.sendTestEmail.mutate({
        params: { id: endUserId },
        body: {},
      })
    },
    onError: (error) => {
      console.error('Error sending test email:', error)
    },
  })

  const resetForm = () => {
    setFormData({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      sku: '',
    })
  }

  const handleEditUser = (user: EndUser) => {
    setFormData({
      id: user.id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      sku: user.sku || '',
    })
    setIsEditingUser(true)
  }

  const handleDeleteUser = async (userId: string) => {
    if (confirm(t('end_users.confirm_delete'))) {
      try {
        await deleteTestEndUserMutation.mutateAsync(userId)
      } catch (error) {
        console.error('Error deleting user:', error)
        alert(t('end_users.delete_error'))
      }
    }
  }

  const handleClearAnswers = async (userId: string) => {
    if (confirm(t('end_users.confirm_clear_answers'))) {
      try {
        await clearTestEndUserAnswersMutation.mutateAsync(userId)
      } catch (error) {
        console.error('Error clearing answers:', error)
        alert(t('end_users.clear_answers_error'))
      }
    }
  }

  const handelSendTestEmail = async (user: EndUser) => {
    try {
      await sendTestEmailMutation.mutateAsync(user.id)
    } catch (error) {
      console.error('Error sending test email:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSkuChange = (value: string) => {
    setFormData({ ...formData, sku: value })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!campaignId) return

    if (isEditingUser) {
      updateTestEndUserMutation.mutate({
        id: formData.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || null,
        sku: formData.sku || null,
      })
    } else {
      addSingleEndUserMutation.mutate({
        campaignId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || null,
        sku: formData.sku || null,
      })
    }
  }

  if (loading) return <div className="p-8 text-center">{t('loading')}</div>

  const endUsers =
    endUsersResponse?.status === 200
      ? (endUsersResponse.body.endUsers || []).filter((user) => user.isTest)
      : []

  // Define columns for the DataTable
  const columns: ColumnDef<EndUser>[] = [
    {
      accessorFn: (row) => [row.firstName, row.lastName].filter(Boolean).join(' '),
      id: 'name',
      header: () => t('end_users.name'),
      cell: (info) => (
        <span className="text-sm text-gray-100">
          {info.getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: () => t('end_users.email'),
      cell: (info) => (
        <span className="text-sm text-gray-100">
          {(info.getValue() as string) || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'phoneNumber',
      header: () => t('end_users.phone'),
      cell: (info) => (
        <span className="text-sm text-gray-100">
          {(info.getValue() as string) || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'sku',
      header: () => t('end_users.sku'),
      cell: (info) => (
        <span className="text-sm text-gray-100">
          {(info.getValue() as string) || '-'}
        </span>
      ),
    },
    {
      accessorFn: (row) => {
        if (!row.address) return '-'

        // Filter out empty values and join with commas
        const addressParts = [
          row?.address?.city,
          row?.address?.zipCode,
          row?.address?.country,
        ].filter(Boolean)

        return addressParts.length > 0 ? addressParts.join(', ') : '-'
      },
      id: 'address',
      header: () => t('end_users.address'),
      cell: (info) => (
        <span className="text-sm text-gray-100">
          {info.getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'uniqueCode',
      header: () => t('end_users.unique_code'),
      cell: (info) => {
        const uniqueCode = info.getValue() as string
        const row = info.row.original
        return (
          <span className="text-sm text-gray-100 font-mono">
            {uniqueCode} (
            <a
              href={`${import.meta.env.VITE_PUBLIC_URL}/${uniqueCode}`}
              target="_blank"
              className="text-primary hover:underline"
              data-testid={`test-users-open-link-${row.id}`}
            >
              Open
            </a>
            )
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: () => t('end_users.actions'),
      cell: ({ row }) => {
        const user = row.original
        const hasAnswers =
          !!user.questionnaireAnswers || !!user.campaignIncentiveId

        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditUser(user)}
              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
              title={t('end_users.edit')}
              data-testid={`test-users-edit-${user.id}`}
            >
              <PencilSimple size={18} />
            </button>
            {campaign?.typeOfCampaign === 'online' && (
              <button
                onClick={() => handelSendTestEmail(user)}
                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                title={t('end_users.send')}
                disabled={sendTestEmailMutation.isPending}
                data-testid={`test-users-send-email-${user.id}`}
              >
                {sendTestEmailMutation.isPending ? (
                  <Spinner size={18} />
                ) : (
                  <PaperPlaneTilt size={18} />
                )}
              </button>
            )}

            <button
              onClick={() => handleDeleteUser(user.id)}
              className="p-1 text-red-600 hover:bg-red-100 rounded"
              title={t('end_users.delete')}
              disabled={deleteTestEndUserMutation.isPending}
              data-testid={`test-users-delete-${user.id}`}
            >
              <Trash size={18} />
            </button>

            {hasAnswers && (
              <button
                onClick={() => handleClearAnswers(user.id)}
                className="p-1 text-amber-600 hover:bg-amber-100 rounded"
                title={t('end_users.clear_answers')}
                disabled={clearTestEndUserAnswersMutation.isPending}
                data-testid={`test-users-clear-answers-${user.id}`}
              >
                <Eraser size={18} />
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="bg-ui-100 p-8 h-full" data-testid="test-users-container">
      <div className="p-8 sm:rounded-lg bg-white">
        <div className="flex justify-between items-center pb-6">
          <h1 className="font-bold text-3xl leading-none mb-0" data-testid="test-users-title">
            {t('end_users.title')}
          </h1>
          <button
            onClick={() => {
              resetForm()
              setIsAddingUser(true)
            }}
            className="px-4 py-2 bg-primary !text-white rounded-md flex items-center gap-2 disabled:!text-muted-foreground disabled:cursor-not-allowed"
            data-testid="test-users-add-button"
            disabled={isTestDisabled}
          >
            <Plus size={18} /> {t('end_users.add_new')}
          </button>
        </div>

        {/* End Users Table using our reusable DataTable component */}
        <div className="border border-border sm:rounded-lg overflow-auto" data-testid="test-users-table">
          <DataTable columns={columns} data={endUsers} className="w-full" />
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {(isAddingUser || isEditingUser) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="test-users-modal-overlay">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl" data-testid="test-users-modal">
            <h2 className="text-xl font-bold mb-4" data-testid="test-users-modal-title">
              {isEditingUser
                ? t('end_users.edit_user')
                : t('end_users.add_new')}
            </h2>

            <form onSubmit={handleSubmit} data-testid="test-users-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('end_users.first_name')} *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    data-testid="test-user-form-firstname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('end_users.last_name')}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    data-testid="test-user-form-lastname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('end_users.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    data-testid="test-user-form-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('end_users.sku')}
                  </label>
                  <Select onValueChange={handleSkuChange} value={formData.sku}>
                    <SelectTrigger className="w-full" data-testid="test-user-form-sku">
                      <SelectValue placeholder={t('end_users.select_sku')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSKUs.map(
                        (sku: { skuCode: string; name: string }) => {
                          return (
                            <SelectItem
                              key={sku.skuCode}
                              value={sku.skuCode}
                              className="cursor-pointer hover:bg-blue-700 hover:text-white"
                              disabled={
                                campaign?.campaignCategory !== 'lottery'
                              }
                            >
                              {sku.name}
                            </SelectItem>
                          )
                        },
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingUser(false)
                    setIsEditingUser(false)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md"
                  data-testid="test-users-modal-cancel"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={
                    addSingleEndUserMutation.isPending ||
                    updateTestEndUserMutation.isPending
                  }
                  className="px-4 py-2 bg-primary text-white rounded-md flex items-center gap-2"
                  data-testid="test-users-modal-save"
                >
                  {addSingleEndUserMutation.isPending ||
                    updateTestEndUserMutation.isPending ? (
                    <>
                      <Spinner size={18} className="animate-spin" />
                      {t('saving')}
                    </>
                  ) : isEditingUser ? (
                    t('update')
                  ) : (
                    t('save')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
